import {
  faCalendarDays,
  faEnvelope,
  faHouseChimney,
  faPhone,
  faUser,
  faVenusMars,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Select } from "../../components/ui/select";
import { Label } from "../../components/ui/label";
import { FormField } from "../../components/ui/form-field";
import { InputGroup, InputGroupText } from "../../components/ui/input-group";
import { toast } from "../../components/ui/use-toast";
import { useAgeSegments, useGenders } from "../../hooks/catalogs";
import {
  initValidation,
  validate,
  findErrors,
  isEmpty,
  isNotPhone,
  isNotEmail,
  isNotZipcode,
} from "../../hooks/formValidation";
import { CustomerData, emptyCustomerData } from "../../model/CustomerData";
import { CurrentSessionData } from "../../hooks/userSession";
import { authFetch } from "../../rest/authFetch";
import RestrictedAccess from "../../components/RestrictedAccess";

const Customer = ({
  currentSession,
}: {
  currentSession: CurrentSessionData;
}) => {

  const { ageSegments } = useAgeSegments();
  const { genders } = useGenders();

  const [customer, setCustomer] = useState<CustomerData>(emptyCustomerData);
  const [disableSubmit, setDisableSubmit] = useState(false);
  const [validation, setValidation] = useState(
    initValidation("phone", "name", "ageSegment", "email", "gender", "zipcode")
  );

  const params = useParams();
  const navigate = useNavigate();

  const isIdPresent = (object: any) => {
    return object && "id" in object;
  };

  const setField = (field: string, event: any) => {
    const updatedCustomer: CustomerData = {
      ...customer,
      [field]: event.target.value,
    };
    setCustomer(updatedCustomer);
    updateValidation(updatedCustomer, field);
  };

  useEffect(() => {
    if (!isIdPresent(params)) {
      return;
    }

    const url = `${process.env.REACT_APP_API_BASE_URL}/customer/${params.id}`;
    authFetch(url)
      .then((response) => response.json())
      .then((result) => {
        if (result?.success) {
          setCustomer({
            id: result.data.id,
            phone: result.data.phone,
            name: result.data.customer_name,
            ageSegment: String(result.data.age_segment),
            email: result.data.email,
            gender: result.data.gender,
            zipcode: result.data.zipcode,
          });
        }
      });
  }, [params]);

  const save = () => {
    setDisableSubmit(true);
    const requestOptions = {
      method: "POST",
      headers: { "Content-Type": "text/plain" },
      body: JSON.stringify(customer),
    };
    authFetch(`${process.env.REACT_APP_API_BASE_URL}/customer/save`, requestOptions)
      .then((response) => response.json())
      .then((result) => {
        if (!result.success) {
          setDisableSubmit(false);
        }
        toast({
          variant: result.success ? "default" : "destructive",
          title: "Mensaje",
          description: result.message,
        });
      });
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const isError = updateValidation(customer);
    if (!isError) {
      save();
    }
  };

  const updateValidation = (customer: CustomerData, field = "") => {
    const newValidation = findErrors(
      customer,
      validate(
        "phone",
        isEmpty("Debe capturar un número de teléfono"),
        isNotPhone("El número de teléfono debe tener 10 dígitos")
      ),
      validate("name", isEmpty("Debe capturar un nombre")),
      validate("ageSegment", isEmpty("Debe seleccionar un segmento de edad")),
      validate(
        "email",
        isEmpty("Debe capturar una dirección de correo"),
        isNotEmail("Debe capturar una dirección de correo válida")
      ),
      validate("gender", isEmpty("Debe seleccionar un género")),
      validate(
        "zipcode",
        isEmpty("Debe capturar el código postal"),
        isNotZipcode("El código postal debe tener 5 dígitos")
      )
    );
    const isValidField =
      !!newValidation.fields[field] && !!validation.fields[field];
    setValidation(
      isValidField
        ? {
            ...validation,
            fields: {
              ...validation.fields,
              [field]: newValidation.fields[field],
            },
          }
        : newValidation
    );
    return newValidation.isError;
  };

  return (
    <RestrictedAccess currentSession={currentSession}>
      <div className="space-y-4">
        <div>
          <h5 className="text-lg font-semibold">Información del Cliente</h5>
          <hr className="my-2" />
        </div>
        <form noValidate onSubmit={handleSubmit} className="space-y-4">
          <FormField>
            <Label htmlFor="phone">Número de teléfono:</Label>
            <InputGroup>
              <InputGroupText className="rounded-r-none border-r-0">
                <FontAwesomeIcon icon={faPhone} />
              </InputGroupText>
              <Input
                id="phone"
                type="text"
                placeholder="##########"
                maxLength={10}
                value={customer.phone}
                aria-invalid={validation.fields["phone"].isError}
                className={
                  validation.fields["phone"].isError
                    ? "rounded-l-none border-destructive focus-visible:ring-destructive"
                    : "rounded-l-none"
                }
                onChange={(event) => setField("phone", event)}
                disabled={isIdPresent(params)}
              />
            </InputGroup>
            {validation.fields["phone"].isError && (
              <p className="text-sm text-destructive">
                {validation.fields["phone"].message}
              </p>
            )}
          </FormField>
          <FormField>
            <Label htmlFor="name">Nombre(s):</Label>
            <InputGroup>
              <InputGroupText className="rounded-r-none border-r-0">
                <FontAwesomeIcon icon={faUser} />
              </InputGroupText>
              <Input
                id="name"
                type="text"
                placeholder="Nombre del Cliente"
                value={customer.name}
                aria-invalid={validation.fields["name"].isError}
                className={
                  validation.fields["name"].isError
                    ? "rounded-l-none border-destructive focus-visible:ring-destructive"
                    : "rounded-l-none"
                }
                onChange={(event) => setField("name", event)}
              />
            </InputGroup>
            {validation.fields["name"].isError && (
              <p className="text-sm text-destructive">
                {validation.fields["name"].message}
              </p>
            )}
          </FormField>
          <FormField>
            <Label htmlFor="email">Correo:</Label>
            <InputGroup>
              <InputGroupText className="rounded-r-none border-r-0">
                <FontAwesomeIcon icon={faEnvelope} />
              </InputGroupText>
              <Input
                id="email"
                type="email"
                placeholder="usuario@correo.com"
                value={customer.email}
                aria-invalid={validation.fields["email"].isError}
                className={
                  validation.fields["email"].isError
                    ? "rounded-l-none border-destructive focus-visible:ring-destructive"
                    : "rounded-l-none"
                }
                onChange={(event) => setField("email", event)}
              />
            </InputGroup>
            {validation.fields["email"].isError && (
              <p className="text-sm text-destructive">
                {validation.fields["email"].message}
              </p>
            )}
          </FormField>
          <FormField>
            <Label htmlFor="zipcode">Código postal:</Label>
            <InputGroup>
              <InputGroupText className="rounded-r-none border-r-0">
                <FontAwesomeIcon icon={faHouseChimney} />
              </InputGroupText>
              <Input
                id="zipcode"
                type="text"
                maxLength={5}
                placeholder="#####"
                value={customer.zipcode}
                aria-invalid={validation.fields["zipcode"].isError}
                className={
                  validation.fields["zipcode"].isError
                    ? "rounded-l-none border-destructive focus-visible:ring-destructive"
                    : "rounded-l-none"
                }
                onChange={(event) => setField("zipcode", event)}
              />
            </InputGroup>
            {validation.fields["zipcode"].isError && (
              <p className="text-sm text-destructive">
                {validation.fields["zipcode"].message}
              </p>
            )}
          </FormField>
          <FormField>
            <Label htmlFor="ageSegment">Segmento de edad:</Label>
            <InputGroup>
              <InputGroupText className="rounded-r-none border-r-0">
                <FontAwesomeIcon icon={faCalendarDays} />
              </InputGroupText>
              <Select
                id="ageSegment"
                value={customer.ageSegment}
                aria-invalid={validation.fields["ageSegment"].isError}
                className={
                  validation.fields["ageSegment"].isError
                    ? "rounded-l-none border-destructive focus-visible:ring-destructive"
                    : "rounded-l-none"
                }
                onChange={(event) => setField("ageSegment", event)}
              >
                <option disabled value=""></option>
                {ageSegments.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.label}
                  </option>
                ))}
              </Select>
            </InputGroup>
            {validation.fields["ageSegment"].isError && (
              <p className="text-sm text-destructive">
                {validation.fields["ageSegment"].message}
              </p>
            )}
          </FormField>
          <FormField>
            <Label htmlFor="gender">Género:</Label>
            <InputGroup>
              <InputGroupText className="rounded-r-none border-r-0">
                <FontAwesomeIcon icon={faVenusMars} />
              </InputGroupText>
              <Select
                id="gender"
                value={customer.gender}
                aria-invalid={validation.fields["gender"].isError}
                className={
                  validation.fields["gender"].isError
                    ? "rounded-l-none border-destructive focus-visible:ring-destructive"
                    : "rounded-l-none"
                }
                onChange={(event) => setField("gender", event)}
              >
                <option disabled value=""></option>
                {genders.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.label}
                  </option>
                ))}
              </Select>
            </InputGroup>
            {validation.fields["gender"].isError && (
              <p className="text-sm text-destructive">
                {validation.fields["gender"].message}
              </p>
            )}
          </FormField>
          <hr className="my-2" />
          <div className="flex justify-center gap-4">
            <Button type="button" variant="secondary" onClick={() => navigate("/")}>
              Regresar
            </Button>
            <Button type="submit" disabled={disableSubmit}>
              Guardar
            </Button>
          </div>
        </form>
      </div>
    </RestrictedAccess>
  );
};

export default Customer;
